import React from "react";
import { withTranslation, type WithTranslation } from "react-i18next";

import { Button, Loader } from "../ui";
import { formatShortDate } from "helper/date";
import { formatBytes } from "helper/file";

export interface DocumentLibraryItem {
  id: number | string;
  title?: string;
  description?: string;
  lang?: string;
  extension?: string;
  publication_start?: string;
  size?: number;
}

interface DocumentLibraryListProps extends WithTranslation {
  isLoading: boolean;
  items: DocumentLibraryItem[];
  onDownload: (id: number | string) => void;
  onPreview: (item: DocumentLibraryItem) => void;
}

function DocumentLibraryList({
  t,
  i18n,
  items,
  isLoading,
  onDownload,
  onPreview,
}: DocumentLibraryListProps) {
  if (isLoading) {
    return (
      <div className="DocumentLibraryState">
        <Loader />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="DocumentLibraryState">
        <p className="ListMessage">{t("document_library.empty")}</p>
      </div>
    );
  }

  return (
    <div className="DocumentLibraryGrid documents-grid">
      {items.map((item) => (
        <article
          className="DocumentLibraryCard document-card"
          id={`document-${item.id}`}
          key={item.id}
        >
          <div className="DocumentLibraryCard-content">
            <span className="DocumentLibraryCard-format doc-type-badge">
              {String(item.extension).toUpperCase()}
            </span>
            <h4 className="DocumentLibraryCard-title">{item.title}</h4>
            {item.description ? (
              <p className="DocumentLibraryCard-description">{item.description}</p>
            ) : null}
            <p className="DocumentLibraryCard-meta doc-meta">
              {t("document_library.publication", {
                date: formatShortDate(item.publication_start, i18n.resolvedLanguage),
              })}{" "}
              • {formatBytes(item.size)}
            </p>
          </div>
          <div className="DocumentLibraryCard-actions">
            <Button
              variant="secondary"
              className="download-btn download-btn--secondary"
              onClick={(event) => {
                event.stopPropagation();
                onPreview(item);
              }}
            >
              {t("document_library.preview")}
            </Button>
            <Button
              className="download-btn"
              onClick={(event) => {
                event.stopPropagation();
                onDownload(item.id);
              }}
            >
              {t("document_library.download")}
            </Button>
          </div>
        </article>
      ))}
    </div>
  );
}

export default withTranslation()(DocumentLibraryList);
